/**
 * Which links may be followed without an interstitial.
 *
 * The allowlist was a hardcoded array of one company's hostnames sitting inside
 * the message renderer. A shared package cannot hold that, so the host supplies
 * it — and the default is the strictest useful answer rather than the most
 * permissive one: relative paths only.
 */

export type IsInternalLink = (url: string) => boolean;

// Dummy origin used only to ask the URL parser where a leading-slash href would
// land. `.invalid` is reserved and never resolves.
const RELATIVE_RESOLVE_BASE = "https://relative-check.invalid";
const RELATIVE_RESOLVE_HOST = new URL(RELATIVE_RESOLVE_BASE).hostname;

/**
 * Build a link check for a set of trusted hostnames.
 *
 * Hostnames are matched exactly. A suffix match would let `evil-getcaruso.com`
 * and `getcaruso.com.attacker.net` through, which is the whole reason this is
 * parsed rather than string-matched.
 */
export function createIsInternalLink(
	allowedHostnames: readonly string[] = [],
): IsInternalLink {
	const allowed = new Set(allowedHostnames);

	return (url: string): boolean => {
		try {
			// A leading slash is not enough. WHATWG treats `\` as `/` for special
			// schemes, so `/\evil.com` becomes protocol-relative and leaves the page.
			// Resolve against a dummy base and require the hostname not to change.
			if (url.startsWith("/")) {
				return (
					new URL(url, RELATIVE_RESOLVE_BASE).hostname === RELATIVE_RESOLVE_HOST
				);
			}

			const parsed = new URL(url);
			if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
				return false;
			}
			return allowed.has(parsed.hostname);
		} catch {
			// Unparseable. Treated as external, which is the safe direction.
			return false;
		}
	};
}

/** Relative paths only. The default when a host supplies no allowlist. */
export const isRelativeLinkOnly: IsInternalLink = createIsInternalLink();
